import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  ActionIcon,
  Modal,
  Textarea,
  Button,
  Card,
  ScrollArea,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconEye, IconPhone, IconMail, IconVideo, IconMapPin, IconCalendar, IconCheck, IconX } from '@tabler/icons-react';
import { useCompanies, useUpdateCompany } from '@/hooks/useCompanies';
import { notifications } from '@mantine/notifications';
import type { CompanyWithDetails, CompanyStatus, ContactPreference, DemoActivity } from '@/types/company';

const DEMO_STATUSES: CompanyStatus[] = [
  'DEMO_REQUESTED',
  'DEMO_CONTACTED',
  'DEMO_SCHEDULED',
  'DEMO_COMPLETED',
];

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  DEMO_REQUESTED: { label: 'Neu', color: 'orange' },
  DEMO_CONTACTED: { label: 'Kontaktiert', color: 'blue' },
  DEMO_SCHEDULED: { label: 'Termin', color: 'cyan' },
  DEMO_COMPLETED: { label: 'Abgeschlossen', color: 'green' },
  DEMO_DECLINED: { label: 'Abgelehnt', color: 'red' },
};

const CONTACT_ICONS: Record<ContactPreference, React.ReactNode> = {
  phone: <IconPhone size={14} />,
  email: <IconMail size={14} />,
  video: <IconVideo size={14} />,
  inperson: <IconMapPin size={14} />,
};

const CONTACT_LABELS: Record<ContactPreference, string> = {
  phone: 'Telefonisch',
  email: 'Per E-Mail',
  video: 'Videocall',
  inperson: 'Vor Ort',
};

const CONTACT_ACTIONS: Record<ContactPreference, { contacted: string; scheduled: string; completed: string }> = {
  phone: { contacted: 'Angerufen', scheduled: 'Rückruf vereinbart', completed: 'Telefonat abgeschlossen' },
  email: { contacted: 'E-Mail gesendet', scheduled: 'Termin per Mail vereinbart', completed: 'Demo abgeschlossen' },
  video: { contacted: 'Link gesendet', scheduled: 'Videocall vereinbart', completed: 'Videocall abgeschlossen' },
  inperson: { contacted: 'Kontaktiert', scheduled: 'Besuch vereinbart', completed: 'Besuch abgeschlossen' },
};

interface ActionModalProps {
  company: CompanyWithDetails | null;
  action: 'contact' | 'schedule' | 'complete' | 'decline' | 'convert' | null;
  onClose: () => void;
  onConfirm: (note: string, scheduledAt?: Date) => void;
}

function ActionModal({ company, action, onClose, onConfirm }: ActionModalProps) {
  const [note, setNote] = useState('');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);

  if (!company || !action) return null;

  const contactPref = (company.contactPreference || 'phone') as ContactPreference;
  const actionLabels = CONTACT_ACTIONS[contactPref];

  const titles: Record<string, string> = {
    contact: actionLabels.contacted,
    schedule: actionLabels.scheduled,
    complete: actionLabels.completed,
    decline: 'Anfrage ablehnen',
    convert: 'In Registrierung umwandeln',
  };

  const needsSchedule = action === 'schedule';

  const handleConfirm = () => {
    if (needsSchedule && !scheduledAt) {
      notifications.show({ title: 'Fehler', message: 'Bitte Termin auswählen', color: 'red' });
      return;
    }
    onConfirm(note, scheduledAt || undefined);
    setNote('');
    setScheduledAt(null);
  };

  return (
    <Modal opened onClose={onClose} title={titles[action]} size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          {company.name} - {[company.contactFirstName, company.contactLastName].filter(Boolean).join(' ')}
        </Text>

        {needsSchedule && (
          <DateTimePicker
            label="Termin"
            placeholder="Datum und Uhrzeit wählen"
            value={scheduledAt}
            onChange={setScheduledAt}
            minDate={new Date()}
          />
        )}

        <Textarea
          label="Notiz"
          placeholder="Optionale Notiz zur Aktion..."
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={3}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button
            color={action === 'decline' ? 'red' : action === 'convert' ? 'green' : 'blue'}
            onClick={handleConfirm}
          >
            Bestätigen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface DemoCardProps {
  company: CompanyWithDetails;
  onAction: (company: CompanyWithDetails, action: 'contact' | 'schedule' | 'complete' | 'decline' | 'convert') => void;
}

function DemoCard({ company, onAction }: DemoCardProps) {
  const navigate = useNavigate();
  const contactPref = (company.contactPreference || 'phone') as ContactPreference;
  const actionLabels = CONTACT_ACTIONS[contactPref];

  const getNextActions = (): Array<{ key: string; label: string; icon: React.ReactNode; color: string; action: 'contact' | 'schedule' | 'complete' | 'decline' | 'convert' }> => {
    switch (company.status) {
      case 'DEMO_REQUESTED':
        return [
          { key: 'contact', label: actionLabels.contacted, icon: CONTACT_ICONS[contactPref], color: 'blue', action: 'contact' },
          { key: 'decline', label: 'Ablehnen', icon: <IconX size={14} />, color: 'red', action: 'decline' },
        ];
      case 'DEMO_CONTACTED':
        return [
          { key: 'schedule', label: 'Termin', icon: <IconCalendar size={14} />, color: 'cyan', action: 'schedule' },
          { key: 'decline', label: 'Ablehnen', icon: <IconX size={14} />, color: 'red', action: 'decline' },
        ];
      case 'DEMO_SCHEDULED':
        return [
          { key: 'complete', label: 'Abschließen', icon: <IconCheck size={14} />, color: 'green', action: 'complete' },
          { key: 'decline', label: 'Absagen', icon: <IconX size={14} />, color: 'red', action: 'decline' },
        ];
      case 'DEMO_COMPLETED':
        return [
          { key: 'convert', label: 'Registrieren', icon: <IconCheck size={14} />, color: 'green', action: 'convert' },
          { key: 'decline', label: 'Kein Interesse', icon: <IconX size={14} />, color: 'red', action: 'decline' },
        ];
      default:
        return [];
    }
  };

  const actions = getNextActions();

  return (
    <Card shadow="sm" padding="sm" radius="md" withBorder mb="xs">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={500} size="sm" lineClamp={1}>{company.name}</Text>
          <ActionIcon variant="subtle" size="sm" onClick={() => navigate(`/companies/${company.id}`)}>
            <IconEye size={14} />
          </ActionIcon>
        </Group>

        <Group gap="xs">
          <Badge size="xs" variant="light" leftSection={CONTACT_ICONS[contactPref]}>
            {CONTACT_LABELS[contactPref]}
          </Badge>
          {company.source && (
            <Badge size="xs" variant="outline" color="gray">
              {company.source === 'restaurant' ? 'Restaurant' : 'Händler'}
            </Badge>
          )}
        </Group>

        <Text size="xs" c="dimmed" lineClamp={1}>
          {[company.contactFirstName, company.contactLastName].filter(Boolean).join(' ')}
        </Text>
        {company.phone && (
          <Text size="xs" c="dimmed">{company.phone}</Text>
        )}
        <Text size="xs" c="dimmed" lineClamp={1}>{company.email}</Text>

        {company.demoSchedule?.scheduledAt && (
          <Text size="xs" c="cyan">
            <IconCalendar size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {new Date(company.demoSchedule.scheduledAt).toLocaleString('de-DE')}
          </Text>
        )}

        <Group gap="xs" mt="xs">
          {actions.map((act) => (
            <Button
              key={act.key}
              size="compact-xs"
              variant="light"
              color={act.color}
              leftSection={act.icon}
              onClick={() => onAction(company, act.action)}
            >
              {act.label}
            </Button>
          ))}
        </Group>
      </Stack>
    </Card>
  );
}

export function DemoKanban() {
  const { data: companies } = useCompanies();
  const updateCompany = useUpdateCompany();
  const [actionModal, setActionModal] = useState<{
    company: CompanyWithDetails;
    action: 'contact' | 'schedule' | 'complete' | 'decline' | 'convert';
  } | null>(null);

  const demoCompanies = (companies ?? []).filter((c) =>
    c.status.startsWith('DEMO_')
  );

  const getCompaniesForStatus = (status: CompanyStatus) =>
    demoCompanies.filter((c) => c.status === status);

  const handleAction = (company: CompanyWithDetails, action: 'contact' | 'schedule' | 'complete' | 'decline' | 'convert') => {
    setActionModal({ company, action });
  };

  const handleConfirmAction = (note: string, scheduledAt?: Date) => {
    if (!actionModal) return;

    const { company, action } = actionModal;
    const contactPref = (company.contactPreference || 'phone') as ContactPreference;
    const actionLabels = CONTACT_ACTIONS[contactPref];

    const newActivity: DemoActivity = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      action: '',
      note: note || undefined,
    };

    let newStatus: CompanyStatus = company.status;
    let newSchedule = company.demoSchedule;

    switch (action) {
      case 'contact':
        newStatus = 'DEMO_CONTACTED';
        newActivity.action = actionLabels.contacted;
        break;
      case 'schedule':
        newStatus = 'DEMO_SCHEDULED';
        newActivity.action = actionLabels.scheduled;
        if (scheduledAt) {
          newSchedule = {
            scheduledAt: scheduledAt.toISOString(),
            scheduledType: contactPref,
          };
        }
        break;
      case 'complete':
        newStatus = 'DEMO_COMPLETED';
        newActivity.action = actionLabels.completed;
        break;
      case 'decline':
        newStatus = 'DEMO_DECLINED';
        newActivity.action = 'Abgelehnt';
        break;
      case 'convert':
        newStatus = 'pending';
        newActivity.action = 'In Registrierung umgewandelt';
        break;
    }

    const existingActivities = company.demoActivities ?? [];

    updateCompany.mutate(
      {
        id: company.id,
        data: {
          status: newStatus,
          demoActivities: [...existingActivities, newActivity],
          ...(newSchedule && { demoSchedule: newSchedule }),
        },
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Aktualisiert',
            message: `${company.name} wurde aktualisiert`,
            color: 'green',
          });
          setActionModal(null);
        },
        onError: () => {
          notifications.show({
            title: 'Fehler',
            message: 'Status konnte nicht aktualisiert werden',
            color: 'red',
          });
        },
      }
    );
  };

  return (
    <>
      <ScrollArea>
        <Group align="flex-start" gap="md" wrap="nowrap" style={{ minWidth: 'max-content' }}>
          {DEMO_STATUSES.map((status) => {
            const config = STATUS_CONFIG[status] ?? { label: status, color: 'gray' };
            const statusCompanies = getCompaniesForStatus(status);

            return (
              <Paper
                key={status}
                withBorder
                p="sm"
                radius="md"
                style={{ width: 280, minHeight: 400, backgroundColor: 'var(--mantine-color-gray-0)' }}
              >
                <Group justify="space-between" mb="md">
                  <Group gap="xs">
                    <Badge color={config.color} variant="filled" size="sm">
                      {config.label}
                    </Badge>
                    <Text size="xs" c="dimmed">({statusCompanies.length})</Text>
                  </Group>
                </Group>

                <Stack gap="xs">
                  {statusCompanies.map((company) => (
                    <DemoCard key={company.id} company={company} onAction={handleAction} />
                  ))}
                  {statusCompanies.length === 0 && (
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                      Keine Einträge
                    </Text>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Group>
      </ScrollArea>

      <ActionModal
        company={actionModal?.company ?? null}
        action={actionModal?.action ?? null}
        onClose={() => setActionModal(null)}
        onConfirm={handleConfirmAction}
      />
    </>
  );
}
